import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { getCurrentUser, fetchUserAttributes, signOut } from '@aws-amplify/auth';
import { getAllMSPs, getAllTenants } from '../utils/adminApi';
import CreateMSP from './CreateMSP';
import MSPList from './MSPList';
import AllTenants from './AllTenants';
import toastr from 'toastr';

const AdminDashboard = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      await getCurrentUser();
      const attributes = await fetchUserAttributes();
      setCurrentUser({ attributes });
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="d-flex">
      {/* Sidebar */}
      <div className="bg-dark text-white" style={{ width: '250px', minHeight: '100vh' }}>
        <div className="p-3">
          <h5 className="mb-0">ODIR Admin</h5>
          <small className="text-muted">System Administration</small>
        </div>
        
        <nav className="nav flex-column p-3">
          <Link to="/dashboard" className="nav-link text-white">
            <i className="fas fa-tachometer-alt me-2"></i>
            Overview
          </Link>
          <Link to="/dashboard/msps" className="nav-link text-white">
            <i className="fas fa-building me-2"></i>
            MSPs
          </Link>
          <Link to="/dashboard/create-msp" className="nav-link text-white">
            <i className="fas fa-plus me-2"></i>
            Create MSP
          </Link>
          <Link to="/dashboard/tenants" className="nav-link text-white">
            <i className="fas fa-users me-2"></i>
            All Tenants
          </Link>
        </nav>

        <div className="mt-auto p-3">
          <div className="dropdown">
            <button className="btn btn-outline-light btn-sm dropdown-toggle w-100" data-bs-toggle="dropdown">
              {currentUser?.attributes?.email || 'Admin'}
            </button>
            <ul className="dropdown-menu">
              <li><button className="dropdown-item" onClick={handleLogout}>Logout</button></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-grow-1 p-4">
        <Routes>
          <Route path="/" element={<AdminOverview />} />
          <Route path="/msps" element={<MSPList />} />
          <Route path="/create-msp" element={<CreateMSP />} />
          <Route path="/tenants" element={<AllTenants />} />
        </Routes>
      </div>
    </div>
  );
};

const AdminOverview = () => {
  const [stats, setStats] = useState({ msps: 0, tenants: 0, mspManaged: 0, selfManaged: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [mspsData, tenantsData] = await Promise.all([
        getAllMSPs(),
        getAllTenants()
      ]);
      
      setStats({
        msps: mspsData.length,
        tenants: tenantsData.length,
        mspManaged: tenantsData.filter(t => t.managedBy === 'msp').length,
        selfManaged: tenantsData.filter(t => t.managedBy === 'self').length
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2>Admin Overview</h2>
      <div className="row">
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Total MSPs</h5>
              <h3 className="text-primary">{loading ? '...' : stats.msps}</h3>
              <p className="text-muted">Managed Service Providers</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Total Tenants</h5>
              <h3 className="text-success">{loading ? '...' : stats.tenants}</h3>
              <p className="text-muted">Customer Accounts</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">MSP-Managed</h5>
              <h3 className="text-warning">{loading ? '...' : stats.mspManaged}</h3>
              <p className="text-muted">Managed by MSPs</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Self-Managed</h5>
              <h3 className="text-info">{loading ? '...' : stats.selfManaged}</h3>
              <p className="text-muted">Direct customers</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;